import { useCallback, useEffect, useState } from "react";
import Immutable from "immutable";
import * as d3Zoom from "d3-zoom";
import {
  ApiEntity,
  AtlasValues,
  BaseRequest,
  IdKeyType,
  ProjectionsList,
} from "@hokuto/jam-core";
import { ZoomControlsSchema, JamCanvasStatus, TilesProps } from "../types.js";
import { useBase } from "./base.js";
import { getTilesProps } from "../utils/tiles.js";
import { useCanvasParams } from "./params.js";
import { ParamsObserver } from "../utils/index.js";

export function getChildrenParams(args: {
  transformation: d3Zoom.ZoomTransform;
  controls: ZoomControlsSchema<HTMLCanvasElement>;
}) {
  const { transformation, controls } = args;
  const { k: scale, x: left, y: top } = transformation;
  const { initialSize } = controls;
  const { width: currentWidth, height: currentHeight } =
    controls.element.getBoundingClientRect();

  const width = Math.ceil(initialSize.width * scale);
  const height = Math.ceil(initialSize.height * scale);

  const right = currentWidth - left - width;
  const bottom = currentHeight - top - height;

  const margin = {
    top: Math.floor(top),
    right: Math.ceil(right),
    bottom: Math.ceil(bottom),
    left: Math.floor(left),
  };

  return {
    width,
    height,
    scale,
    margin,
  };
}

export function useCanvas(
  args: Parameters<typeof useBase>["0"] &
    BaseRequest &
    Pick<TilesProps, "tileSize" | "tileIncrement"> & {
      [IdKeyType.Map]?: string;
      [IdKeyType.Atlas]?: string;
      [ApiEntity.Atlas]?: AtlasValues;
      projection?: ProjectionsList;
      paramsObserver?: ParamsObserver;
    }
) {
  const { paramsObserver, tileSize, tileIncrement, ...props } = args;
  const [childrenParams, setChildrenParams] =
    useState<ReturnType<typeof getChildrenParams>>();
  const canvasProps = childrenParams
    ? { ...props, ...childrenParams }
    : { ...props };

  const baseProps = useBase(canvasProps);

  const updateChildrenParams = useCallback(
    (current: ReturnType<typeof getChildrenParams>) => {
      setChildrenParams((previous) => {
        if (!previous) {
          return current;
        }
        if (
          Immutable.fromJS(previous).hashCode() ===
          Immutable.fromJS(current).hashCode()
        )
          return previous;
        return current;
      });
    },
    []
  );

  const [tilesProps, setTilesProps] =
    useState<ReturnType<typeof getTilesProps>>();

  useEffect(() => {
    if (!childrenParams) return;
    const tilesProps = getTilesProps({
      ...childrenParams,
      tileSize,
      tileIncrement,
    });
    setTilesProps(tilesProps);
  }, [childrenParams, tileSize, tileIncrement]);

  const params = {
    ...props,
    ...baseProps,
    paramsObserver,
  };

  const { mapParams, atlasParams } = useCanvasParams(params);

  const canvasStatus: JamCanvasStatus | undefined = tilesProps && {
    tileSize: tilesProps.tileSize,
    scale: 1 / baseProps.scale,
    x: -tilesProps.margin.left / baseProps.scale,
    y: -tilesProps.margin.top / baseProps.scale,
    width: tilesProps.width,
    height: tilesProps.height,
    bgColor: baseProps.bgColor,
  };

  return {
    baseProps,
    tilesProps,
    canvasStatus,
    childrenParams,
    mapParams,
    atlasParams,
    setChildrenParams: updateChildrenParams,
  };
}
